import { useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  formatInstant,
  links,
  resolveFormattingLocale,
  useAuth,
  useListSearch,
} from '@eis/core/reporting';
import {
  InfiniteDataGrid,
  PageHeader,
  PageLayout,
  PropertyFilter,
  RequirePermission,
  cardSurfaceSx,
} from '@eis/patterns';
import type { GridColDef } from '@mui/x-data-grid';
import { reportingPermissions as p } from '../../../permissions';
import { newReport, type DatasetRef, type ReportType } from '../../../lib/document';
import { codeMessage } from '../../../lib/codes';
import {
  documentViolationsOf,
  reportingReasonOf,
  useCreateReport,
  useFetchDatasetFields,
  useFilterCatalog,
  useInfiniteList,
} from '../api/reports';
import { toReportRows, type ReportRow } from '../model/rows';
import { DatasetPicker, useDatasetOptions } from './DatasetPicker';

const SORT_KEYS: Readonly<Record<string, string>> = {
  name: 'name',
  reportType: 'reportType',
  updatedAt: 'updatedAt',
};

/**
 * Berichte (SDD-024 L-1; mockup board 2): the workspace's reports and the caller's own in one list —
 * `InfiniteDataGrid` + `PropertyFilter` over the service's catalog, state in the URL, keyset paging.
 * Another user's private report is never a row (R-3). "Neuer Bericht" starts a private draft on a
 * dataset; "Importieren (JSON)" is the AI-generation path (D-5): the document is checked and every
 * violation is listed.
 */
export function ReportsListPage(): ReactNode {
  const { t, i18n } = useTranslation('reporting');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const url = useListSearch();
  const catalog = useFilterCatalog('reports');
  const list = useInfiniteList('reports', {
    ...(url.params.filters === undefined ? {} : { filters: url.params.filters }),
    ...(url.params.search === undefined ? {} : { search: url.params.search }),
    ...(url.params.order === undefined ? {} : { order: url.params.order }),
  });
  const rows = useMemo(() => toReportRows(list.items), [list.items]);
  const [importing, setImporting] = useState(false);
  const [creating, setCreating] = useState(false);

  // The minimum widths sum to ~910 px: the list fits the content area at 1280 px without a
  // horizontal scrollbar, whose overlay would cover the last row (prompt 60 phase 60.9).
  const columns = useMemo<GridColDef<ReportRow>[]>(
    () => [
      { field: 'name', headerName: t('list.columns.name'), flex: 1.4, minWidth: 160 },
      {
        field: 'reportType',
        headerName: t('list.columns.reportType'),
        width: 120,
        valueFormatter: (v: string) => (v === '' ? '' : t(`types.${v as ReportType}`)),
      },
      {
        field: 'datasetName',
        headerName: t('list.columns.datasetName'),
        flex: 1,
        minWidth: 110,
        sortable: false,
      },
      {
        field: 'publishedVersion',
        headerName: t('list.columns.version'),
        width: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', height: '100%' }}>
            {row.publishedVersion > 0 && (
              <Chip
                size="small"
                label={t('version.published', { version: row.publishedVersion })}
              />
            )}
            {row.draftVersion > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={
                  row.publishedVersion > 0
                    ? t('version.draft', { version: row.draftVersion })
                    : t('version.draftOnly')
                }
              />
            )}
          </Stack>
        ),
      },
      {
        field: 'visibility',
        headerName: t('list.columns.visibility'),
        width: 120,
        sortable: false,
        valueFormatter: (v: string) =>
          v === '' ? '' : t(`visibility.${v as 'PRIVATE' | 'WORKSPACE'}`),
      },
      {
        field: 'author',
        headerName: t('list.columns.author'),
        flex: 0.6,
        minWidth: 100,
        sortable: false,
      },
      {
        field: 'updatedAt',
        headerName: t('list.columns.updatedAt'),
        width: 140,
        valueFormatter: (v: string) =>
          v === '' ? '' : formatInstant(v, locale, undefined, { precise: false }),
      },
    ],
    [t, locale],
  );

  const header = (
    <PageHeader
      title={t('list.title')}
      description={t('list.subtitle', { tenant: tenant?.name ?? tenant?.id ?? '' })}
      actions={
        <RequirePermission anyOf={[p.reports.create]}>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setImporting(true);
              }}
            >
              {t('list.import')}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('list.new')}
            </Button>
          </Stack>
        </RequirePermission>
      }
    />
  );

  return (
    <PageLayout header={header}>
      <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
        <Stack spacing={2}>
          <PropertyFilter
            catalog={catalog.data}
            value={{ filters: url.params.filters ?? '', search: url.params.search ?? '' }}
            onChange={(next) => {
              url.set({ filters: next.filters, search: next.search });
            }}
          />
          <InfiniteDataGrid
            rows={rows}
            list={list}
            columns={columns}
            ariaLabel={t('list.ariaLabel')}
            order={url.params.order}
            onOrderChange={(order) => {
              url.set({ order });
            }}
            sortKeys={SORT_KEYS}
            errorDescription={t('list.error')}
            emptyTitle={t('list.empty.title')}
            emptyDescription={t('list.empty.description')}
            onRowClick={(row) => {
              void navigate({ href: links.reporting.report(row.code) });
            }}
          />
        </Stack>
      </Paper>
      <ImportDialog
        open={importing}
        onClose={() => {
          setImporting(false);
        }}
        onCreated={(code) => {
          void navigate({ href: links.reporting.editReport(code) });
        }}
      />
      <NewReportDialog
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
        onCreated={(code) => {
          void navigate({ href: links.reporting.editReport(code) });
        }}
      />
    </PageLayout>
  );
}

function ImportDialog({
  open,
  onClose,
  onCreated,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCreated: (code: string) => void;
}): ReactNode {
  const { t } = useTranslation('reporting');
  const create = useCreateReport();
  const [text, setText] = useState('');
  const [parseError, setParseError] = useState(false);
  const reason = create.error === null ? undefined : reportingReasonOf(create.error);
  const violations = create.error === null ? [] : documentViolationsOf(create.error);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="import-title">
      <DialogTitle id="import-title">{t('importDialog.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText variant="body2">{t('importDialog.description')}</DialogContentText>
        <TextField
          multiline
          fullWidth
          minRows={10}
          maxRows={20}
          size="small"
          sx={{ mt: 2 }}
          label={t('importDialog.label')}
          value={text}
          slotProps={{ htmlInput: { spellCheck: false, style: { fontFamily: 'monospace' } } }}
          onChange={(event) => {
            setText(event.target.value);
            setParseError(false);
            create.reset();
          }}
          {...(parseError ? { error: true, helperText: t('importDialog.notJson') } : {})}
        />
        {reason !== undefined && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="import-refusal">
            <AlertTitle>
              {reason} — {codeMessage(t, reason).title}
            </AlertTitle>
            <Typography variant="body2">{t('importDialog.refused')}</Typography>
            {violations.map((v) => (
              <Typography key={`${v.field}:${v.description}`} variant="body2" component="div">
                <code>{v.field}</code> — {v.description}
              </Typography>
            ))}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          {t('importDialog.cancel')}
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={create.isPending || text.trim() === ''}
          onClick={() => {
            let document: Record<string, unknown>;
            try {
              document = JSON.parse(text) as Record<string, unknown>;
            } catch {
              setParseError(true);
              return;
            }
            create.mutate(
              { document },
              {
                onSuccess: (report) => {
                  onCreated(report.code ?? '');
                },
              },
            );
          }}
        >
          {t('importDialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** "Neuer Bericht": a private draft on a dataset (also the datasets page's "Bericht mit diesem Datensatz"). */
export function NewReportDialog({
  open,
  onClose,
  onCreated,
  dataset,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCreated: (code: string) => void;
  /** Preset by "Bericht mit diesem Datensatz". */
  readonly dataset?: DatasetRef;
}): ReactNode {
  const { t } = useTranslation('reporting');
  const create = useCreateReport();
  const fieldsOf = useFetchDatasetFields();
  const { options } = useDatasetOptions();
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState('');
  const [type, setType] = useState<ReportType>('PIVOT');
  const ref = dataset ?? options.find((o) => o.key === chosen)?.ref;
  const reason = create.error === null ? undefined : reportingReasonOf(create.error);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="new-report-title"
    >
      <DialogTitle id="new-report-title">{t('newDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            size="small"
            label={t('newDialog.name')}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            required
          />
          {dataset === undefined && (
            <DatasetPicker
              value={chosen}
              onChange={(option) => {
                setChosen(option?.key ?? '');
              }}
            />
          )}
          <TextField
            select
            size="small"
            label={t('newDialog.type')}
            value={type}
            onChange={(event) => {
              setType(event.target.value as ReportType);
            }}
          >
            {(['TABLE', 'PIVOT', 'CHART'] as const).map((k) => (
              <MenuItem key={k} value={k}>
                {t(`types.${k}`)}
              </MenuItem>
            ))}
          </TextField>
          {reason !== undefined && <Alert severity="error">{codeMessage(t, reason).title}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          {t('newDialog.cancel')}
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={create.isPending || name.trim() === '' || ref === undefined}
          onClick={() => {
            if (ref === undefined) return;
            void fieldsOf(ref).then((fields) => {
              create.mutate(
                {
                  document: newReport(name.trim(), ref, type, fields[0]) as unknown as Record<
                    string,
                    unknown
                  >,
                },
                {
                  onSuccess: (report) => {
                    onCreated(report.code ?? '');
                  },
                },
              );
            });
          }}
        >
          {t('newDialog.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * "Bericht mit diesem Datensatz" (boards 7 and 8): a private PIVOT draft on the dataset, named after
 * it, opened in the editor at once — the shelves are where the report gets its shape.
 */
export function useReportWithDataset(): {
  readonly start: (dataset: DatasetRef, name: string) => void;
  readonly isPending: boolean;
} {
  const create = useCreateReport();
  const fieldsOf = useFetchDatasetFields();
  const navigate = useNavigate();
  return {
    isPending: create.isPending,
    start: (dataset, name) => {
      void fieldsOf(dataset).then((fields) => {
        create.mutate(
          {
            document: newReport(name, dataset, 'PIVOT', fields[0]) as unknown as Record<
              string,
              unknown
            >,
          },
          {
            onSuccess: (report) => {
              void navigate({ href: links.reporting.editReport(report.code ?? '') });
            },
          },
        );
      });
    },
  };
}
